import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import isEqual from "lodash/isEqual";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { GridSortModel, GridCellParams, GridColDef, GridSortItem } from "@mui/x-data-grid";
import { Grid } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt, faPlus } from "@fortawesome/free-solid-svg-icons";

import TableHeader from "../../../containers/TableHeader/TableHeader";
import SearchTableMessage from "../../SearchTableMessage/SearchTableMessage";
import AddEditItem from "../AddEditItem/AddEditItem";
import RemoveItem from "../RemoveItem/RemoveItem";

import { Page } from "../../../models/api/page";
import { ListSortDirection } from "../../../models/api/sort";
import { RefModel } from "../../../models/base/ref-model";
import { SearchOptions } from "../../../models/search-options/search-options";

import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";

import useSearchOptions from "../../../custom-hooks/useSearchOptions/useSearchOptions";

import { TableProps, State } from "./interfaces";
import { DataGrid, Actions, AddButtonLink, EditButtonLink, RenderButtonBlock } from "../styles";
import { DTOModel } from "../../../models/base/dto-model";
// import { AddBankCashTransaction } from "../../../views/process-accounting/BankAndCash/AddBankCashTransaction/AddBankCashTransaction";
import { CashTransactionsFilter } from "../../../models/cash-transaction/cash-transactions.filter";
// import { AddBookedAcountingTransactions } from "../../../views/process-accounting/BankAndCash/AddBookedAmount/AddBookedAcountingTransactions/AddBookedAcountingTransactions";
// import UnlinkTransaction from "../../../views/process-accounting/BankAndCash/AddBookedAmount/UnlinkTransaction/UnlinkTransaction";
import AddEditTransaction from "../../../containers/AddTransaction/AddEditTransaction/AddEditTransaction";
import UnlinkTransaction from "../../../views/process-accounting/BankAndCashOverview/BankAndCash/AddBookedAmount/UnlinkTransaction/UnlinkTransaction";
import { AddBookedAcountingTransactions } from "../../../views/process-accounting/BankAndCashOverview/BankAndCash/AddBookedAmount/AddBookedAcountingTransactions/AddBookedAcountingTransactions";
import { AddBankCashTransaction } from "../../../views/process-accounting/BankAndCashOverview/BankAndCash/AddBankCashTransaction/AddBankCashTransaction";

const CustomTable = ({
  currItem,
  renderButtonProp,
  translationRootPath,
  tableHeight = 450,
  defaultPageSize = 5,
  rowsPerPageOptions = [2, 5, 10, 20, 50, 100],
  rowHeight = 60,
  columns,
  serviceApi,
  loadItemsPath,
  saveItemsPath,
  updateGlobalStateCallback,
  propsFilter,
  readOnly,
  allowEdit,
  formFields,
  verticalOrientation,
  defaultItem,
  filter,
  quickFilters,
  addEditType = "form",
  addEditPageRootPath,
  CustomAddEditComponent,
  changeInsteadDelete,
  shouldReload,
  notifyParent,
  autoHeight = false,
  checkedCandidates,
  unmounted,
  setNewIds,
  mutatedTransactions,
  setNewTransactionsIds,
  transactionsIdsProps,
}: TableProps) => {
  const { t } = useTranslation();
  const [selectionModel, setSelectionModel] = useState(checkedCandidates?.current.map((el: any) => el.id));
  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(filter);

  const [state, setState] = useState<State>({
    items: [],
    totalItems: 0,
    loading: false,
    error: "",
    defaultItem: null,
    page: {
      pageNumber: 0,
      pageSize: defaultPageSize,
    },
  });

  useEffect(() => {
    if (defaultItem) setState((state) => ({ ...state, defaultItem }));
  }, [defaultItem]);

  const updateGlobalState = useCallback(
    () => updateGlobalStateCallback && updateGlobalStateCallback(),
    [updateGlobalStateCallback]
  );

  // initialized serviceApi
  const serviceApiInstance = useMemo(() => {
    const serviceApiObj = serviceApi;

    if (loadItemsPath) serviceApiObj.useLoadItemsPath(loadItemsPath);
    if (saveItemsPath) serviceApiObj.useSaveItemsPath(saveItemsPath);

    return serviceApiObj;
  }, [serviceApi, loadItemsPath, saveItemsPath]);

  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    {
      field: serviceApiInstance ? serviceApiInstance.defaultSort[0].name : "",
      sort: serviceApiInstance ? (serviceApiInstance.defaultSort[0].direction === 0 ? "asc" : "desc") : "asc",
    },
  ]);

  const handleChangeSortModel = (model: GridSortModel) => {
    if (!isEqual(sortModel, model) && model.length) setSortModel(model);
  };

  const loadData = useCallback(
    async (page: Page, searchOptions: SearchOptions, sortModel: GridSortItem[], signal?: AbortSignal) => {
      const { field, sort } = sortModel[0];
      const { Ascending, Descending } = ListSortDirection;

      const response = await serviceApiInstance.loadItems(
        {
          isDeleted: searchOptions.deletedItems,
          ...(propsFilter ?? {}),
        } as CashTransactionsFilter,
        searchOptions.query,
        [{ name: field, direction: sort === "asc" ? Ascending : Descending }],
        page,
        searchOptions.exactMatch,
        60,
        signal
      );

      toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");

      return response;
    },
    [serviceApiInstance, propsFilter]
  );

  const loadDataCallback = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if ((propsFilter?.legalEntityIds && propsFilter?.cashStatementIds?.length === 0) || propsFilter === "empty") {
        setState((state) => ({
          ...state,
          items: [],
          totalItems: 0,
          loading: false,
          error: "",
          defaultItem: null,
          page: {
            pageNumber: 0,
            pageSize: defaultPageSize,
          },
        }));
        return;
      }

      if (unmounted?.current) {
        unmounted.current = false;
        return;
      }

      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadData(
          { pageNumber: state.page.pageNumber, pageSize: state.page.pageSize },
          searchOptions,
          sortModel,
          signal
        );

        if (!signal || !signal.aborted) {
          setState((state) => ({ ...state, error, items, totalItems }));
        }
      } catch (error: any) {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, error: error?.message ?? error }));
      } finally {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, loading: false }));
      }
    },
    [loadData, propsFilter, state.page.pageNumber, state.page.pageSize, searchOptions, sortModel]
  );

  useEffect(() => {
    if (serviceApiInstance) {
      let timer: ReturnType<typeof setTimeout>;
      const abortController = new AbortController();

      timer = setTimeout(() => loadDataCallback(abortController.signal), searchOptions.query.length === 0 ? 0 : 1000);

      return () => {
        clearTimeout(timer);
        abortController.abort();
      };
    }
  }, [loadDataCallback, searchOptions.query, searchOptions.exactMatch]);

  useEffect(() => {
    if (shouldReload) {
      let timer: ReturnType<typeof setTimeout>;
      const abortController = new AbortController();

      timer = setTimeout(() => loadDataCallback(abortController.signal), searchOptions.query.length === 0 ? 0 : 1000);

      notifyParent && notifyParent(false);

      return () => {
        clearTimeout(timer);
        abortController.abort();
      };
    }
  }, [shouldReload]);

  const handleChangePage = (param: keyof Page, value: number) =>
    setState((state) => ({
      ...state,
      page: {
        pageNumber: param === "pageNumber" ? value : 0,
        pageSize: param === "pageSize" ? value : state.page.pageSize,
      },
    }));

  const handleResetDefaultItem = () => {
    if (defaultItem) setState((state) => ({ ...state, defaultItem }));
  };

  const handleCancel = (resetItem: RefModel, effect: "add" | "edit") => {
    if (effect === "add") handleResetDefaultItem();

    if (effect === "edit") {
      setState((state) => ({
        ...state,
        items: state.items.map((item: RefModel) => (item.id === resetItem.id ? resetItem : item)),
      }));
    }
  };

  const repetitivePropsForEditComponent = {
    titleForEditComponent: t(`${translationRootPath}.title_for_edit_component`),
    formFields,
    verticalOrientation,
    handleCancel,
    allowEdit,
    serviceApiInstance,
    updateGlobalState: updateGlobalState,
    handleReload: loadDataCallback,
  };

  const editColumn: GridColDef = {
    field: "actions",
    headerName: t("buttons.actions"),
    headerAlign: "center",
    disableColumnMenu: true,
    sortable: false,
    width: 100,
    renderCell: ({ row }: GridCellParams) => (
      <Actions>
        {addEditType === "form" && (
          <AddEditItem
            effect="edit"
            item={row as DTOModel}
            {...repetitivePropsForEditComponent}
            isMutated={mutatedTransactions}
          >
            <FontAwesomeIcon icon={faPencilAlt} />
          </AddEditItem>
        )}
        {addEditType === "page" && (
          <EditButtonLink to={`${addEditPageRootPath}/${row.id}?new=false`}>
            <FontAwesomeIcon icon={faPencilAlt} />
          </EditButtonLink>
        )}
        {addEditType === "custom" && !!CustomAddEditComponent ? (
          <CustomAddEditComponent
            effect="edit"
            item={row}
            handleNotifyParent={loadDataCallback}
          />
        ) : null}
        {renderButtonProp === "bookedTransactions" ? (
          <UnlinkTransaction
            currItem={currItem}
            item={row as DTOModel}
            serviceApiInstance={serviceApiInstance}
            handleReload={loadDataCallback}
          />
        ) : (
          <RemoveItem
            items={[row as DTOModel]}
            itemName={row["name"] ?? row["description"]}
            serviceApiInstance={serviceApiInstance}
            handleReload={loadDataCallback}
            changeInsteadDelete={changeInsteadDelete}
            isMutated={mutatedTransactions}
          />
        )}
      </Actions>
    ),
  };

  return (
    <>
      <TableHeader
        searchOptions={searchOptions}
        handleChangeSearchOptions={handleChangeSearchOptions}
        tableTitle={t(`${translationRootPath}.table_title`)}
        quickFilters={quickFilters}
        buttonToRender={
          !readOnly &&
          (renderButtonProp === "bookedTransactions" ? (
            <RenderButtonBlock>
              <AddBookedAcountingTransactions
                item={currItem}
                handleNotifyParent={() => {}}
                isBankTransaction={!!currItem?.bankAccount}
              />
              <AddEditTransaction
                effect="add"
                returnTransaction={true}
                handleNotifyParent={(transaction) => {
                  setNewIds && setNewIds(transaction);
                  notifyParent && notifyParent(true);
                }}
                legalEntity={currItem?.legalEntity}
                date={currItem?.date}
                buttonVariant="plusButton"
              />
            </RenderButtonBlock>
          ) : currItem ? (
            <AddBankCashTransaction
              currItem={currItem}
              handleNotifyParent={() => loadDataCallback()}
              buttonVariant="plusButton"
              noBankAndCashAccounts
            />
          ) : addEditType === "page" ? (
            <AddButtonLink to={`${addEditPageRootPath}/${uuidv4()}?new=true`}>
              <FontAwesomeIcon icon={faPlus} />
            </AddButtonLink>
          ) : addEditType === "custom" ? (
            CustomAddEditComponent && <CustomAddEditComponent handleNotifyParent={loadDataCallback} />
          ) : (
            renderButtonProp !== "bookedAccountingTransactions" && (
              <AddEditItem
                item={state.defaultItem}
                handleResetDefaultItem={handleResetDefaultItem}
                buttonVariant="outlined"
                {...repetitivePropsForEditComponent}
              >
                <FontAwesomeIcon icon={faPlus} />
              </AddEditItem>
            )
          ))
        }
      />
      <Grid style={{ height: `${tableHeight}px`, width: "100%" }}>
        {state.totalItems === 0 ? (
          <SearchTableMessage
            loading={state.loading}
            searchOptions={searchOptions}
            error={state.error}
          />
        ) : (
          <DataGrid
            paginationMode="server"
            pageSize={state.page.pageSize}
            onPageSizeChange={(value) => handleChangePage("pageSize", value)}
            rowsPerPageOptions={rowsPerPageOptions}
            page={state.page.pageNumber}
            onPageChange={(value) => handleChangePage("pageNumber", value)}
            rowHeight={rowHeight}
            rows={state.items}
            rowCount={state.totalItems}
            sortingMode="server"
            sortingOrder={["asc", "desc"]}
            sortModel={sortModel}
            onSortModelChange={handleChangeSortModel}
            loading={state.loading}
            error={state.error === "" ? null : state.error}
            getRowHeight={autoHeight ? () => "auto" : undefined}
            checkboxSelection={!!checkedCandidates?.current}
            selectionModel={selectionModel}
            onSelectionModelChange={(ids) => {
              const selectedIDs = new Set(ids);
              const selectedRows = state.items.filter((row) => selectedIDs.has(row.id));

              if (checkedCandidates) {
                const checkedRows = [...checkedCandidates.current, ...selectedRows];

                checkedCandidates.current = Array.from(new Set(checkedRows.map((a) => a.id)))
                  .map((id) => {
                    return checkedRows.find((a) => a.id === id);
                  })
                  .filter((el) => selectedIDs.has(el.id));
              }
              setSelectionModel(checkedCandidates?.current.map((el: any) => el.id));
            }}
            keepNonExistentRowsSelected
            columns={[...columns, ...(!readOnly || allowEdit ? [editColumn] : [])]}
          />
        )}
      </Grid>
    </>
  );
};

export default memo(CustomTable);
